import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Shield, 
  Phone, 
  User, 
  Lock,
  CheckCircle,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KYC = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [step, setStep] = useState(1);
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [demoOtp, setDemoOtp] = useState('');
  const [bvnNin, setBvnNin] = useState('');

  useEffect(() => {
    fetchKycStatus();
  }, []);

  const fetchKycStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/kyc/status`, { headers: getAuthHeaders() });
      setKycStatus(res.data);
      if (res.data.is_verified) {
        setStep(4);
      } else if (res.data.phone_verified) {
        setPhoneVerified(true);
        setStep(3);
      }
      if (res.data.kyc_full_name) setFullName(res.data.kyc_full_name);
      if (res.data.kyc_phone) setPhone(res.data.kyc_phone);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = step === 4 ? 100 : Math.round(((step - 1) / 3) * 100);

  const handleSendOtp = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/kyc/send-otp`, { phone }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setOtpSent(true);
      if (res.data.demo_otp) setDemoOtp(res.data.demo_otp);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/kyc/verify-otp`, { code: otp }, { headers: getAuthHeaders() });
      toast.success('Phone number verified successfully');
      setPhoneVerified(true);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code. Please check and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitKyc = async () => {
    if (!fullName.trim()) {
      toast.error('Full name is required');
      return;
    }
    if (!bvnNin || bvnNin.length !== 11 || !/^\d+$/.test(bvnNin)) {
      toast.error('Please enter a valid 11-digit BVN or NIN');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/kyc/submit`, {
        full_name: fullName, bvn_or_nin: bvnNin
      }, { headers: getAuthHeaders() });
      toast.success(res.data.message);
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#0B1F3A]" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="kyc-screen">
      <div className="app-container px-4 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate('/profile')} className="p-2 rounded-xl hover:bg-slate-100">
            <ArrowLeft size={20} className="text-[#0B1F3A]" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#0B1F3A]">Verify Your Account</h1>
            <p className="text-sm text-slate-500">Complete verification to unlock full access</p>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6" data-testid="kyc-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-[#0B1F3A]">Verification Progress</span>
            <span className="text-sm font-bold text-[#2272C5]">{progressPercent}% Complete</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Step 1: Full Name */}
        {step >= 1 && step < 4 && (
          <Card className="glass-card mb-4" data-testid="kyc-step-1">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2.5 rounded-xl ${step === 1 ? 'bg-[#0B1F3A]' : 'bg-emerald-500'}`}>
                  <User size={18} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#0B1F3A]">Full Name</p>
                  <p className="text-xs text-slate-500">As it appears on your ID</p>
                </div>
                {step > 1 && <CheckCircle size={20} className="text-emerald-500" />}
              </div>
              {step === 1 && (
                <div className="space-y-3">
                  <Input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                    className="h-12 rounded-xl"
                    data-testid="kyc-fullname-input"
                  />
                  <Button
                    onClick={() => { if (fullName.trim()) setStep(2); else toast.error('Please enter your name'); }}
                    className="w-full h-12 rounded-xl bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white"
                    data-testid="kyc-name-continue"
                  >
                    Continue
                  </Button>
                </div>
              )}
              {step > 1 && <p className="text-sm text-slate-600 font-medium">{fullName}</p>}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Phone Verification */}
        {step >= 2 && step < 4 && (
          <Card className="glass-card mb-4" data-testid="kyc-step-2">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2.5 rounded-xl ${step === 2 && !phoneVerified ? 'bg-[#0B1F3A]' : phoneVerified ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                  <Phone size={18} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#0B1F3A]">Phone Verification</p>
                  <p className="text-xs text-slate-500">We'll send a code to verify</p>
                </div>
                {phoneVerified && <CheckCircle size={20} className="text-emerald-500" />}
              </div>
              {step === 2 && !phoneVerified && (
                <div className="space-y-3">
                  {!otpSent ? (
                    <>
                      <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Enter phone number"
                        className="h-12 rounded-xl"
                        data-testid="kyc-phone-input"
                      />
                      <Button
                        onClick={handleSendOtp}
                        disabled={submitting}
                        className="w-full h-12 rounded-xl bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white"
                        data-testid="kyc-send-otp"
                      >
                        {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                        Verify Phone Number
                      </Button>
                    </>
                  ) : (
                    <>
                      <Label className="text-slate-600 text-sm">Enter the 6-digit code sent to {phone}</Label>
                      {demoOtp && (
                        <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                          <p className="text-xs text-amber-700 font-semibold">Demo Mode — OTP Code: <span className="font-black text-amber-900">{demoOtp}</span></p>
                        </div>
                      )}
                      <Input
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit code"
                        className="h-12 rounded-xl text-center text-lg tracking-widest"
                        maxLength={6}
                        data-testid="kyc-otp-input"
                      />
                      <Button
                        onClick={handleVerifyOtp}
                        disabled={submitting}
                        className="w-full h-12 rounded-xl bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white"
                        data-testid="kyc-verify-otp"
                      >
                        {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                        Verify Code
                      </Button>
                      <button
                        onClick={() => { setOtpSent(false); setOtp(''); setDemoOtp(''); }}
                        className="w-full text-sm text-[#2272C5] font-semibold"
                      >
                        Resend Code
                      </button>
                    </>
                  )}
                </div>
              )}
              {phoneVerified && <p className="text-sm text-slate-600 font-medium">{phone} — Verified</p>}
            </CardContent>
          </Card>
        )}

        {/* Step 3: BVN/NIN */}
        {step >= 3 && step < 4 && (
          <Card className="glass-card mb-4" data-testid="kyc-step-3">
            <CardContent className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className={`p-2.5 rounded-xl ${step === 3 ? 'bg-[#0B1F3A]' : 'bg-slate-300'}`}>
                  <Lock size={18} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-[#0B1F3A]">BVN or NIN</p>
                  <p className="text-xs text-slate-500">11-digit Bank Verification or National ID number</p>
                </div>
              </div>
              {step === 3 && (
                <div className="space-y-3">
                  <Input
                    value={bvnNin}
                    onChange={(e) => setBvnNin(e.target.value.replace(/\D/g, '').slice(0, 11))}
                    placeholder="Enter 11-digit BVN or NIN"
                    className="h-12 rounded-xl"
                    maxLength={11}
                    data-testid="kyc-bvn-input"
                  />
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-2">
                    <Shield size={14} className="text-[#2272C5] mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-slate-600">Your BVN/NIN is encrypted and will never be shared with other users.</span>
                  </div>
                  <Button
                    onClick={handleSubmitKyc}
                    disabled={submitting}
                    className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                    data-testid="kyc-submit"
                  >
                    {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Shield size={16} className="mr-2" />}
                    Submit Verification
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <Card className="glass-card mb-4 border-2 border-emerald-200" data-testid="kyc-success">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={40} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-black text-[#0B1F3A] mb-2">Verification Successful</h2>
              <p className="text-slate-600 mb-6">Your account has been successfully verified. You can now join and create savings groups.</p>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 font-bold text-sm mb-6">
                <CheckCircle size={14} />
                Verified User
              </div>
              <Button
                onClick={() => navigate('/profile')}
                className="w-full h-12 rounded-xl bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white"
                data-testid="kyc-back-profile"
              >
                Back to Profile
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default KYC;
